import React, { useEffect, useMemo, useState } from 'react';

import { Search } from 'lucide-react';
import { useNavigate } from 'react-router';
import ClientBottomNav from '../../components/ClientBottomNav.tsx';
import { baseurly } from '../../utils/apis.ts';
import { X_API_KEY} from '../../utils/apis.ts';
import {useAuth} from "../../context/authContext.tsx";
import { baseurl } from '../../utils/apis.ts';
import MainLayout from '../../layouts/MainLayout.tsx';
import { digitalOceanImageUrl } from "../../utils/digitalOcean.ts";



interface VehicleModel {
  id: number;
  model_name: string;
  vehicle_type: string;
  image: string;
  year_make: number | string | null;
}

const TARGET_MODEL_YEAR = 2026;
const RECENT_MODELS_STORAGE_KEY = "client_recent_vehicle_models";
const MAX_RECENT_MODELS = 6;
const YEAR_FOCUS_LIMIT = 5;

const parseModelYear = (value: number | string | null): number | null => {
  const parsedYear = Number(value);
  return Number.isFinite(parsedYear) ? parsedYear : null;
};

const DashboardPage: React.FC = () => {
  const {employeeI, authReady} = useAuth()
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState<VehicleModel[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [recentlyOpenedIds, setRecentlyOpenedIds] = useState<number[]>(() => {
    if (typeof window === "undefined") return [];
    try {
      const storedIds = window.localStorage.getItem(RECENT_MODELS_STORAGE_KEY);
      if (!storedIds) return [];

      const parsedIds = JSON.parse(storedIds);
      if (!Array.isArray(parsedIds)) return [];

      return parsedIds
        .filter((id): id is number => typeof id === "number")
        .slice(0, MAX_RECENT_MODELS);
    } catch {
      return [];
    }
  });

  // Fetch vehicle models from the API
  useEffect(() => {
    if (!authReady) {
      return;
    }
    if (!employeeI) {
      navigate('/login', {replace: true});
      return;
    }
    // fetchVehicleModels().then()

    // fetch(`${baseurl}api/vehicle_models`)

    fetch(`${baseurly}/vehicle_models`,{
        
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-API-KEY': X_API_KEY,
      },
      credentials: "include",
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error("Failed to fetch vehicle models");
        }
        return res.json();
      })
      .then((data) => {
        setVehicles(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching vehicle models:", err);
        setError("Unable to load vehicle models");
        setLoading(false);
      });
      
  }, [authReady, employeeI, navigate]);


  // const fetchVehicleModels = async () => {
      
  //   await axiosL.get('vehicle_models', {
  //     withCredentials: true,})
  //   .then((res) => {
  //     setVehicles(res.data);
  //     setLoading(false);
  //   })
  //   .catch((err) => {
  //     console.error('Error fetching vehicle models:', err);
  //     setError('Unable to load vehicle models');
  //     setLoading(false);
  //   });
  // };

  const rememberRecentlyOpened = (modelId: number) => {
    setRecentlyOpenedIds((previousIds) => {
      const nextIds = [modelId, ...previousIds.filter((id) => id !== modelId)].slice(
        0,
        MAX_RECENT_MODELS
      );
      try {
        window.localStorage.setItem(RECENT_MODELS_STORAGE_KEY, JSON.stringify(nextIds));
      } catch {
        // Ignore storage errors and keep navigation working.
      }
      return nextIds;
    });
  };

  const handleVehicleClick = (vehicle: VehicleModel) => {
    rememberRecentlyOpened(vehicle.id);
    navigate(`/client/vehicle/${vehicle.id}`);
  };

  const sortedVehicles = useMemo(() => {
    return [...vehicles].sort((a, b) => {
      const yearA = parseModelYear(a.year_make);
      const yearB = parseModelYear(b.year_make);
      const distanceA = yearA === null ? Number.POSITIVE_INFINITY : Math.abs(yearA - TARGET_MODEL_YEAR);
      const distanceB = yearB === null ? Number.POSITIVE_INFINITY : Math.abs(yearB - TARGET_MODEL_YEAR);

      if (distanceA !== distanceB) return distanceA - distanceB;
      if (yearA !== yearB) return (yearB ?? Number.NEGATIVE_INFINITY) - (yearA ?? Number.NEGATIVE_INFINITY);
      return a.model_name.localeCompare(b.model_name);
    });
  }, [vehicles]);

  const vehicleMap = useMemo(() => {
    const map = new Map<number, VehicleModel>();
    vehicles.forEach((vehicle) => map.set(vehicle.id, vehicle));
    return map;
  }, [vehicles]);

  const recentlyOpenedVehicles = useMemo(
    () =>
      recentlyOpenedIds
        .map((modelId) => vehicleMap.get(modelId))
        .filter((vehicle): vehicle is VehicleModel => vehicle !== undefined),
    [recentlyOpenedIds, vehicleMap]
  );

  const modelSnapshot = useMemo(() => {
    return vehicles.reduce(
      (summary, vehicle) => {
        const year = parseModelYear(vehicle.year_make);
        summary.totalModels += 1;
        if (year === null) {
          summary.missingYear += 1;
          return summary;
        }

        if (year === TARGET_MODEL_YEAR) summary.targetYear += 1;
        if (Math.abs(year - TARGET_MODEL_YEAR) <= 1) summary.nearTarget += 1;
        if (year > TARGET_MODEL_YEAR) summary.futureYear += 1;
        return summary;
      },
      { totalModels: 0, targetYear: 0, nearTarget: 0, futureYear: 0, missingYear: 0 }
    );
  }, [vehicles]);

  const yearFocusVehicles = useMemo(
    () =>
      sortedVehicles
        .filter((vehicle) => parseModelYear(vehicle.year_make) !== null)
        .slice(0, YEAR_FOCUS_LIMIT),
    [sortedVehicles]
  );

  return (
    <MainLayout>
      <div className="min-h-screen bg-blue-900">
        {/* Search Bar */}
        <div className="px-6  py-7 bg-blue-900">
          <h1 className="flex justify-center text-white text-2xl font-bold mb-4  ">Welcome {employeeI?.fname} !</h1>
          <div className="relative">
            <input
              type="text"
              placeholder="Search for an instruction document..."
              className="w-full py-3 px-4 pr-10 rounded-full"
            />
            <Search className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-900" />
          </div>
        </div>
    
        {/* Work Instructions Card */}
        <div className="bg-white ring-2 ring-white-500 rounded-t-3xl">
          <div className="px-6 mb-6 bg-white rounded-full p-6">
            <div className="bg-white rounded-xl border p-8 flex justify-between items-center">
              <div>
                <h2 className="text-blue-900 text-xl font-bold mb-2">
                  Production Processes Portal
                </h2>
                <p className="text-gray-600 text-sm max-w-xs">
                  Find clear, step-by-step instructions to help you complete your tasks efficiently and accurately.
                </p>
              </div>
              <img
                src={digitalOceanImageUrl("products/kayoola_coach/images/orders-coach/carousel.png")}
                alt="Bus illustration"
                className="w-27 h-24"
              />
            </div>
          </div>
          {/* Vehicle Models Section */}
          <div className="px-6 mb-6 bg-white">
            <h2 className="text-blue-900 text-xl font-bold mb-4">Vehicle Models</h2>

            {loading ? (
              <p className="text-gray-600">Loading vehicle models...</p>
            ) : error ? (
              <p className="text-red-500">{error}</p>
            ) : vehicles.length > 0 ? (
              <div className="overflow-x-auto">
                <div className="flex gap-4 pb-4">
                 
                  
                  {sortedVehicles.map((vehicle) => (
                    
                    <div

                      key={vehicle.id}
                      className="bg-white border rounded-xl p-4 min-w-[200px] cursor-pointer hover:shadow-lg transition-shadow"
                      onClick={() => handleVehicleClick(vehicle)}
                    >
                      <img
                        src={ 
                          vehicle.image 
                          ?
                          `${baseurl}files/${vehicle.image}`
                          :
                          digitalOceanImageUrl("assets/images/b.png")
                        }
                        alt={vehicle.model_name}
                        className="w-full h-32 object-cover rounded-lg mb-2"
                      />
                      <h3 className="font-bold text-sm">{vehicle.model_name}</h3>
                      <div className='flex gap-2'>
                      <p className="text-sm text-gray-600">{vehicle.vehicle_type}</p>
                      <p className="text-sm text-gray-600">{vehicle.year_make}</p>
                      </div>
                      

                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-gray-600">
                No vehicle models added yet. Contact your administrator
                to add a vehicle.
              </p>
            )}
          </div>

          {/* Model Insights */}
          <div className="px-6 p-9">
            <h2 className="text-blue-900 text-xl font-bold">Model Insights</h2>

            <div className="grid gap-4 mt-4 sm:grid-cols-2 lg:grid-cols-4">
              <div className="bg-blue-100 rounded-xl p-4">
                <p className="text-sm text-blue-900">Total Models</p>
                <p className="text-2xl font-bold text-blue-900">{modelSnapshot.totalModels}</p>
              </div>
              <div className="bg-blue-100 rounded-xl p-4">
                <p className="text-sm text-blue-900">{TARGET_MODEL_YEAR} Models</p>
                <p className="text-2xl font-bold text-blue-900">{modelSnapshot.targetYear}</p>
              </div>
              <div className="bg-blue-100 rounded-xl p-4">
                <p className="text-sm text-blue-900">Within +/-1 Year</p>
                <p className="text-2xl font-bold text-blue-900">{modelSnapshot.nearTarget}</p>
              </div>
              <div className="bg-blue-100 rounded-xl p-4">
                <p className="text-sm text-blue-900">Beyond {TARGET_MODEL_YEAR}</p>
                <p className="text-2xl font-bold text-blue-900">{modelSnapshot.futureYear}</p>
              </div>
            </div>

            <div className="grid gap-4 mt-4 lg:grid-cols-2">
              <div className="bg-blue-100 rounded-xl p-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-bold text-blue-900">{TARGET_MODEL_YEAR} Year Focus</h3>
                  <span className="text-sm text-blue-900">Top {YEAR_FOCUS_LIMIT}</span>
                </div>
                {yearFocusVehicles.length === 0 ? (
                  <p className="text-sm text-gray-600">No vehicle models available yet.</p>
                ) : (
                  <div className="space-y-2">
                    {yearFocusVehicles.map((vehicle) => (
                      <button
                        key={vehicle.id}
                        type="button"
                        className="w-full bg-white rounded-lg p-3 flex items-center justify-between text-left hover:bg-blue-50 transition"
                        onClick={() => handleVehicleClick(vehicle)}
                      >
                        <div>
                          <p className="font-semibold text-blue-900">{vehicle.model_name}</p>
                          <p className="text-sm text-gray-600">{vehicle.vehicle_type}</p>
                        </div>
                        <span className="text-sm font-semibold text-blue-900">
                          {parseModelYear(vehicle.year_make) ?? "N/A"}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="bg-blue-100 rounded-xl p-6">
                <h3 className="text-lg font-bold text-blue-900 mb-3">Recently Opened Models</h3>
                {recentlyOpenedVehicles.length === 0 ? (
                  <p className="text-sm text-gray-600">Open a model to keep it here for quick access.</p>
                ) : (
                  <div className="space-y-2">
                    {recentlyOpenedVehicles.map((vehicle) => (
                      <button
                        key={vehicle.id}
                        type="button"
                        className="w-full bg-white rounded-lg p-3 flex items-center justify-between text-left hover:bg-blue-50 transition"
                        onClick={() => handleVehicleClick(vehicle)}
                      >
                        <div>
                          <p className="font-semibold text-blue-900">{vehicle.model_name}</p>
                          <p className="text-sm text-gray-600">{vehicle.vehicle_type}</p>
                        </div>
                        <span className="text-sm font-semibold text-blue-900">
                          {parseModelYear(vehicle.year_make) ?? "N/A"}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
                {modelSnapshot.missingYear > 0 && (
                  <p className="text-xs text-gray-600 mt-3">
                    {modelSnapshot.missingYear} model(s) have no year and are excluded from year ranking.
                  </p>
                )}
              </div>
            </div>
          </div>

          <ClientBottomNav showBack={true} />
        </div>
      </div>
    </MainLayout>
  );
};

export default DashboardPage;
